import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { countsTowardProgress } from '@/lib/selfPacedSnapshot';

/** Rutas de autoaprendizaje en las que el usuario ya está inscrito (cohorte perpetua). */
export function useMySelfPacedEnrollments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['self-paced-enrollments', user?.id],
    queryFn: async (): Promise<string[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('cohort_participants')
        .select('cohorts!inner(program_id, is_perpetual)')
        .eq('user_id', user.id);
      if (error) throw error;
      return (data ?? [])
        .map((r: any) => r.cohorts)
        .filter((c: any) => c?.is_perpetual)
        .map((c: any) => c.program_id as string);
    },
    enabled: !!user,
  });
}

/** Progreso (%) por ruta de autoaprendizaje inscrita. */
export function useSelfPacedProgress(programIds: string[]) {
  const { user } = useAuth();
  const key = [...programIds].sort().join(',');
  return useQuery({
    queryKey: ['self-paced-progress', user?.id, key],
    queryFn: async (): Promise<Record<string, number>> => {
      if (!user || programIds.length === 0) return {};

      const { data: chapters, error: chErr } = await supabase
        .from('program_chapters')
        .select('id, program_id')
        .in('program_id', programIds);
      if (chErr) throw chErr;
      if (!chapters?.length) return {};

      const { data: links, error: lkErr } = await supabase
        .from('chapter_contents')
        .select('chapter_id, content_id, is_mandatory')
        .in('chapter_id', chapters.map(c => c.id));
      if (lkErr) throw lkErr;
      if (!links?.length) return {};

      const chapterToProgram = new Map(chapters.map(c => [c.id, c.program_id as string]));

      // Snapshot personal por ruta (solo cuentan los recursos que existían al inscribirse)
      const { data: snapRows } = await supabase
        .from('self_paced_required_content')
        .select('content_id, is_mandatory, cohorts!inner(program_id)')
        .eq('user_id', user.id);
      const snapByProgram = new Map<string, Map<string, boolean>>();
      for (const r of (snapRows ?? []) as any[]) {
        const pid = r.cohorts?.program_id as string;
        if (!pid) continue;
        if (!snapByProgram.has(pid)) snapByProgram.set(pid, new Map());
        snapByProgram.get(pid)!.set(r.content_id, r.is_mandatory);
      }
      const contentIds = Array.from(new Set(links.map(l => l.content_id)));

      const { data: progress, error: prErr } = await supabase
        .from('content_progress')
        .select('content_id, completed')
        .eq('user_id', user.id)
        .in('content_id', contentIds);
      if (prErr) throw prErr;

      const done = new Set((progress ?? []).filter(p => p.completed).map(p => p.content_id));
      const totals: Record<string, { total: number; done: number }> = {};
      for (const l of links) {
        const pid = chapterToProgram.get(l.chapter_id);
        if (!pid) continue;
        // Mismo criterio que Inicio y Contenido: solo obligatorios de la copia personal
        if (!countsTowardProgress(snapByProgram.get(pid) ?? null, l.content_id, l.is_mandatory !== false)) continue;
        totals[pid] ??= { total: 0, done: 0 };
        totals[pid].total += 1;
        if (done.has(l.content_id)) totals[pid].done += 1;
      }

      return Object.fromEntries(
        Object.entries(totals).map(([pid, t]) => [pid, t.total ? Math.round((t.done / t.total) * 100) : 0]),
      );
    },
    enabled: !!user && programIds.length > 0,
  });
}

/** Inscribe al usuario en una ruta de autoaprendizaje (requiere plan activo). */
export function useEnrollSelfPacedRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const { data, error } = await supabase.functions.invoke('enroll-self-paced-route', {
        body: { program_id: programId },
      });
      if (error) {
        const ctx: any = (error as any).context;
        let payload: any = null;
        try { payload = await ctx?.json?.(); } catch { /* noop */ }
        const err = new Error(payload?.error || error.message) as Error & { code?: string };
        err.code = payload?.code;
        throw err;
      }
      return data as { ok: boolean; cohort_id: string; already_enrolled: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['self-paced-enrollments'] });
      qc.invalidateQueries({ queryKey: ['my-cohorts'] });
      qc.invalidateQueries({ queryKey: ['all-cohort-enrollments'] });
    },
  });
}

/** Registra el interés en una ruta con acompañamiento (con o sin sesión). */
export function useRegisterRouteInterest() {
  return useMutation({
    mutationFn: async (input: { programId: string; fullName?: string; email?: string; phone: string }) => {
      const { data, error } = await supabase.functions.invoke('register-route-interest', {
        body: {
          program_id: input.programId,
          full_name: input.fullName,
          email: input.email,
          phone: input.phone,
        },
      });
      if (error) {
        const ctx: any = (error as any).context;
        let payload: any = null;
        try { payload = await ctx?.json?.(); } catch { /* noop */ }
        throw new Error(payload?.error || error.message);
      }
      return data as { ok: boolean; id: string | null };
    },
  });
}

/** Snapshot personal del temario (content_id -> obligatorio) de una cohorte perpetua. */
export function useSelfPacedSnapshot(cohortId?: string | null, enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['self-paced-snapshot', user?.id, cohortId],
    enabled: !!user && !!cohortId && enabled,
    queryFn: async (): Promise<Map<string, boolean> | null> => {
      const { data, error } = await supabase
        .from('self_paced_required_content')
        .select('content_id, is_mandatory')
        .eq('user_id', user!.id)
        .eq('cohort_id', cohortId!);
      if (error) throw error;
      if (!data?.length) return null;
      return new Map(data.map(r => [r.content_id, r.is_mandatory]));
    },
  });
}
